import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Tag, FolderOpen, X, Loader2 } from 'lucide-react';

// Bulk tag / topic actions for selected Question Bank questions
export const BulkActionsBar = ({ selectedIds, topics, tags, onDone, onClear }) => {
  const { api } = useAuth();
  const [tagText, setTagText] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);

  if (!selectedIds.length) return null;

  const run = async (payload, msg) => {
    setBusy(true);
    try {
      await api.post('/questions/bulk-update', { ids: selectedIds, ...payload });
      toast.success(msg);
      setTagText('');
      setTopic('');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  const parsedTags = tagText.split(',').map(t => t.trim()).filter(Boolean);

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-[#20acf6]/30 bg-[#20acf6]/5" data-testid="qb-bulk-bar">
      <span className="text-sm font-medium text-[#2a3e5d]" data-testid="qb-selected-count">{selectedIds.length} selected</span>
      <div className="flex items-center gap-1">
        <Input list="qb-bulk-tags" value={tagText} onChange={e => setTagText(e.target.value)} placeholder="Tags, comma-separated" className="h-9 w-48" data-testid="qb-bulk-tag-input" />
        <datalist id="qb-bulk-tags">{tags.map(t => <option key={t} value={t} />)}</datalist>
        <Button size="sm" variant="outline" disabled={busy || !parsedTags.length} onClick={() => run({ add_tags: parsedTags }, 'Tags added')} data-testid="qb-bulk-add-tag-btn">
          <Tag className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
        <Button size="sm" variant="ghost" disabled={busy || !parsedTags.length} onClick={() => run({ remove_tags: parsedTags }, 'Tags removed')} data-testid="qb-bulk-remove-tag-btn">
          Remove
        </Button>
      </div>
      <div className="flex items-center gap-1">
        <Input list="qb-bulk-topics" value={topic} onChange={e => setTopic(e.target.value)} placeholder="Move to topic" className="h-9 w-44" data-testid="qb-bulk-topic-input" />
        <datalist id="qb-bulk-topics">{topics.map(t => <option key={t} value={t} />)}</datalist>
        <Button size="sm" variant="outline" disabled={busy || !topic.trim()} onClick={() => run({ category: topic.trim() }, `Moved to "${topic.trim()}"`)} data-testid="qb-bulk-set-topic-btn">
          <FolderOpen className="h-3.5 w-3.5 mr-1" /> Move
        </Button>
      </div>
      {busy && <Loader2 className="h-4 w-4 animate-spin text-[#20acf6]" />}
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onClear} data-testid="qb-bulk-clear-btn"><X className="h-4 w-4 mr-1" /> Clear</Button>
    </div>
  );
};
